/**
 * Type definitions for the Persona System
 *
 * The persona system provides a unified framework for managing interaction modes and tools.
 * It supports:
 * - Stackable personas with priority-based event handling
 * - Event-driven lifecycle management
 * - Unified tool registration
 * - Multi-view system with scriptable actions
 * - Rich view configurations (camera, shading, rendering, UI state)
 */

import { EventDispatcher } from '../EventDispatcher';
import { Viewer } from '../viewer/viewer';
import { CameraMode, ClipTask } from '../defines';
import type { ViewManager } from './ViewManager';
import type { ToolSettingsSchema } from './ToolSettings';
import type { ToolSettingsPanel } from '../ui/ToolSettingsPanel';
import type { CameraProfile } from './orientation';

// Extract types from const objects
type CameraModeType = (typeof CameraMode)[keyof typeof CameraMode];
type ClipTaskType = (typeof ClipTask)[keyof typeof ClipTask];

/**
 * Shading/rendering mode for point clouds
 *
 * Defines how point clouds are visualized/colored in the viewer.
 */
export enum ShadingMode {
  /** True RGB color from point cloud data */
  RGB = 'RGB',

  /** RGB color blended with elevation-based coloring */
  RGB_HEIGHT = 'RGB_HEIGHT',

  /** Height/elevation gradient coloring */
  HEIGHT = 'HEIGHT',

  /** Laser return intensity */
  INTENSITY = 'INTENSITY',

  /** Intensity with gradient visualization */
  INTENSITY_GRADIENT = 'INTENSITY_GRADIENT',

  /** Point classification categories (ground, vegetation, building, etc.) */
  CLASSIFICATION = 'CLASSIFICATION',

  /** Laser return number (first, second, etc.) */
  RETURN_NUMBER = 'RETURN_NUMBER',

  /** Data source/origin */
  SOURCE = 'SOURCE',

  /** Level of detail visualization (for debugging) */
  LEVEL_OF_DETAIL = 'LEVEL_OF_DETAIL',
}

/**
 * View mode definition
 *
 * A view mode represents a complete visualization configuration including:
 * - Camera settings (perspective/orthographic, position, orientation)
 * - Rendering settings (shading, EDL, point size, etc.)
 * - UI state (map, grid, overlays)
 * - Scriptable actions to execute on entry/exit
 *
 * View modes enable context-aware visualizations that adapt to each tool/persona.
 */
export interface IViewMode {
  /** Unique identifier for this view mode */
  id: string;

  /** Human-readable name for UI display */
  name: string;

  /** Optional description for tooltips */
  description?: string;

  /** View mode: 2D (top-down orthographic) or 3D (perspective) */
  mode?: '2D' | '3D';

  /** Camera projection mode */
  cameraMode?: CameraModeType;

  /**
   * Camera-identity class (see personas/orientation.ts). Views sharing a
   * derived OrientationKey preserve the camera verbatim when switching
   * between each other; undeclared views fall back to inference from
   * cameraMode/planeId.
   */
  cameraProfile?: CameraProfile;

  /** Shading/rendering mode for point clouds */
  shadingMode?: ShadingMode;

  /** Eye-Dome Lighting enabled */
  edlEnabled?: boolean;

  /** EDL strength/intensity (0-1 range) */
  edlStrength?: number;

  /** Point cloud clipping mode */
  clipMode?: ClipTaskType;

  /** Map overlay visibility */
  mapVisible?: boolean;

  /** Grid overlay visibility */
  gridVisible?: boolean;

  /** Point size/scale multiplier */
  pointSize?: number;

  /** Field of view in degrees (for perspective camera) */
  fov?: number;

  /** Camera position override */
  cameraPosition?: { x: number; y: number; z: number };

  /** Camera target/lookAt override */
  cameraTarget?: { x: number; y: number; z: number };

  /**
   * Script to execute when entering this view mode
   *
   * Can be used for:
   * - Custom camera animations
   * - Enabling/disabling specific features
   * - Setting up tool-specific UI elements
   * - Loading additional data
   *
   * @param viewer - The viewer instance
   * @returns Promise for async operations, or void
   */
  onEnter?: (viewer: Viewer) => void | Promise<void>;

  /**
   * Script to execute when exiting this view mode
   *
   * Can be used for:
   * - Cleanup operations
   * - Restoring default settings
   * - Hiding tool-specific UI
   *
   * @param viewer - The viewer instance
   * @returns Promise for async operations, or void
   */
  onExit?: (viewer: Viewer) => void | Promise<void>;

  /**
   * Optional icon identifier for UI
   * Can be icon name, emoji, or URL
   */
  icon?: string;

  /**
   * Optional view context/parameters
   * Used to pass additional state when switching views
   *
   * Currently implemented:
   * - planeId: Identifier for orthographic views (plane, line)
   */
  viewContext?: {
    planeId?: string;
    [key: string]: string | number | boolean | undefined;
  };
}

/**
 * View configuration for a persona or tool
 *
 * Instead of a single view, personas/tools can provide multiple view modes.
 * Users can switch between these views via UI (expanded from simple 2D/3D toggle).
 *
 * Benefits:
 * - Context-optimized visualizations per tool
 * - No need to exit tool to change view
 * - Discover features through view options
 * - Foundation for view presets/favorites
 */
export interface IViewConfig {
  /**
   * Available view modes for this persona/tool
   *
   * - If multiple views provided, UI shows view selector
   * - If single view, it's applied automatically
   * - If empty/undefined, inherits from persona or uses default
   *
   * UI adapts based on count:
   * - 0-1 views: No selector shown
   * - 2 views (2D/3D): Toggle button (backward compatible)
   * - 3+ views: Dropdown menu
   */
  views?: IViewMode[];

  /**
   * Default view mode ID to activate
   * If not specified, uses first view in array
   */
  defaultViewId?: string;
}

/**
 * Base interface all tools must implement
 *
 * Tools are specific interaction capabilities within a persona.
 * They can optionally override view configuration and handle input events.
 *
 * Examples: measure_point, measure_distance, volume_box, cluster
 */
export interface ITool {
  /** Unique tool identifier */
  readonly name: string;

  /** Current enabled state */
  readonly enabled: boolean;

  /**
   * Enable the tool
   * Called when the tool should become active
   */
  enable(): void;

  /**
   * Disable the tool
   * Called when the tool should become inactive
   */
  disable(): void;

  /**
   * Get tool's view configuration
   * Returns available view modes for this tool
   *
   * Tool views can:
   * - Override persona views (by ID)
   * - Extend persona views (add new views)
   * - Provide no views (inherit from persona)
   *
   * @returns View config with available view modes
   */
  getViewConfig?(): IViewConfig;

  /**
   * Get tool settings panel
   * Returns optional panel instance that will be shown when tool is enabled
   *
   * The panel is automatically managed by the React component:
   * - When visible: panel is shown with click-outside detection
   * - When hidden: panel is hidden and listeners are cleaned up
   *
   * @returns Settings panel instance (usually ToolSettingsPanel)
   *
   * @example
   * getSettingsPanel(): any {
   *   // Usually auto-created from getSettingsSchema()
   *   return this.settingsPanelInstance;
   * }
   */
  getSettingsPanel?(): ToolSettingsPanel | undefined;

  /**
   * Get declarative settings schema for this tool
   *
   * Returns optional settings schema that defines the tool's configurable settings.
   * This is the recommended approach for simple settings UIs, as the schema will be
   * automatically rendered in the toolbar settings menu with appropriate controls.
   *
   * The settings schema is a declarative object that defines:
   * - Setting types (slider, toggle, select, number, text, color)
   * - Labels and descriptions
   * - Default values and constraints
   * - Units for numeric values
   *
   * @returns Settings schema object or undefined
   *
   * @example
   * getSettingsSchema?(): ToolSettingsSchema {
   *   return {
   *     sphereRadius: {
   *       type: 'slider',
   *       label: 'Sphere Radius',
   *       description: 'Size of the sphere markers',
   *       defaultValue: 0.5,
   *       min: 0.1,
   *       max: 2.0,
   *       step: 0.1,
   *       unit: 'm'
   *     },
   *     showLabels: {
   *       type: 'toggle',
   *       label: 'Show Labels',
   *       description: 'Display measurement labels',
   *       defaultValue: true
   *     }
   *   };
   * }
   */
  getSettingsSchema?(): ToolSettingsSchema;

  /**
   * When true, the tool-settings menu opens automatically when this tool is
   * enabled (instead of waiting for the gear-button click). Use for tools
   * whose settings are the primary interaction surface — e.g. the sketch-object
   * tool's placement library.
   */
  shouldAutoOpenSettings?(): boolean;

  /**
   * Optional input event handlers
   * Tools can implement these to handle user input
   * Called by PersonaManager based on persona priority
   */
  onMouseDown?(event: MouseEvent): void;
  onMouseMove?(event: MouseEvent): void;
  onMouseUp?(event: MouseEvent): void;
  onKeyDown?(event: KeyboardEvent): void;
  onKeyUp?(event: KeyboardEvent): void;

  /**
   * Cleanup and dispose resources
   * Called when tool is removed or viewer is disposed
   */
  dispose(): void;
}

/**
 * Tool configuration for registration
 *
 * Used to register tools with a persona via factory pattern.
 * Factory pattern allows lazy instantiation and dependency injection.
 */
export interface IToolConfig {
  /** Unique tool identifier */
  id: string;

  /**
   * Factory function to create tool instance
   *
   * @param viewer - The viewer instance
   * @param persona - The parent persona
   * @returns Tool instance
   */
  factory: (viewer: Viewer, persona: IPersona) => ITool;

  /** Enable tool by default when persona is enabled */
  defaultEnabled?: boolean;

  /**
   * Disable tool by default when persona is enabled
   * This is useful for tools that require certain conditions to be met before they can be enabled
   * (e.g., floor selection required before using floorplan tool)
   */
  defaultDisabled?: boolean;

  /** Optional view configuration override */
  viewConfig?: IViewConfig;

  /**
   * If true, disables other tools in persona when enabled
   * Useful for mutually exclusive tools (e.g., different measurement modes)
   */
  exclusive?: boolean;

  /**
   * Icon for the tool
   * Can be an icon name, emoji, or URL
   */
  icon?: string;

  /**
   * Display name for the tool
   * Plain English string used for UI display and as translation key
   */
  name?: string;

  /**
   * Tooltip shown when the tool is disabled
   * Plain English string (e.g., "select a floor first")
   */
  disabledTooltip?: string;

  /**
   * When true, the tool additionally requires the selected floor to have a
   * NON-EMPTY wall sketch before it becomes available (design-tab tools that
   * place OPENING_SKETCHes on wall edges). Enforced by
   * `FloorplanPersona.isToolAvailable` on top of the floor-selection gate;
   * pair with `disabledTooltip: 'create walls first'`.
   */
  requiresWallSketch?: boolean;
}

/**
 * Toolbar element types
 *
 * Toolbar elements represent the different types of items that can appear in a toolbar.
 * This allows for flexible toolbar configurations with tools, spacers, and action buttons.
 */

/**
 * Toolbar element type enum
 */
export enum ToolbarElementType {
  TOOL = 'tool',
  SPACER = 'spacer',
  ACTION = 'action',
}

/**
 * Tool element - represents a tool button in the toolbar
 */
export interface IToolElement {
  type: ToolbarElementType.TOOL;
  config: IToolConfig;
}

/**
 * Spacer element - represents a visual separator in the toolbar
 */
export interface ISpacerElement {
  type: ToolbarElementType.SPACER;
  /** Optional width/size of the spacer */
  size?: 'small' | 'medium' | 'large';
}

/**
 * Action element - represents an action button (like undo/redo)
 */
export interface IActionElement {
  type: ToolbarElementType.ACTION;
  /** Unique identifier for this action */
  id: string;
  /** Icon for the action button */
  icon?: string;
  /** Display name for the action */
  name?: string;
  /** Tooltip text (plain English, used as translation key) */
  tooltip?: string;
  /**
   * Handler function called when the action is clicked
   * @param viewer - The viewer instance
   */
  onClick?: (viewer: Viewer) => void;
  /**
   * Function to determine if the action is disabled
   * @param viewer - The viewer instance
   * @returns True if the action should be disabled
   */
  isDisabled?: (viewer: Viewer) => boolean;
}

/**
 * Toolbar element union type
 *
 * Represents any type of element that can appear in a toolbar.
 */
export type IToolbarElement = IToolElement | ISpacerElement | IActionElement;

/**
 * Persona Mode
 *
 * A mode is a sub-configuration within a persona that contains its own tools.
 * Personas with multiple modes allow users to switch between different tool sets.
 * The first mode is automatically activated when the persona is selected.
 */
export interface IPersonaMode {
  /** Unique mode identifier within the persona */
  id: string;

  /** Display name for the mode (plain English, used as translation key) */
  name: string;

  /** Icon identifier for UI display */
  icon?: string;

  /**
   * Toolbar elements for this mode
   * Defines the complete toolbar layout including tools, spacers, and actions
   */
  toolbarElements?: IToolbarElement[];

  /**
   * Tools available in this mode (legacy format)
   * @deprecated Use toolbarElements instead for more flexible toolbar configuration
   */
  tools?: IToolConfig[];

  /** Optional view configuration override for this mode */
  viewConfig?: IViewConfig;
}

/**
 * Persona configuration
 *
 * Defines a complete interaction mode with tools and view settings.
 * Personas are the top-level organizational unit for tools.
 *
 * Examples: annotation, volume, cluster, plane, floorplan, default
 */
export interface IPersonaConfig {
  /** Unique persona identifier */
  name: string;

  /**
   * Priority for event handling and view application
   * Higher priority personas are checked first for input events
   * @default 0
   */
  priority?: number;

  /** Default view configuration for this persona */
  defaultViewConfig?: IViewConfig;

  /** Modes within this persona - each mode contains its own tools */
  modes: IPersonaMode[];

  /**
   * If true, disables all other personas when this one is enabled
   * Useful for focused modes like annotation or measurement
   * @default false
   */
  exclusive?: boolean;

  /**
   * Optional factory function to create a custom persona instance
   * If not provided, BasePersona will be used
   * @param viewer - Viewer instance
   * @param config - Persona configuration
   * @param viewManager - Shared ViewManager from PersonaManager
   * @returns Custom persona instance
   */
  factory?: (viewer: Viewer, config: IPersonaConfig, viewManager: ViewManager) => IPersona;
}

/**
 * Base interface all personas must implement
 *
 * Personas are interaction modes that contain tools and manage view configuration.
 * They extend EventDispatcher to emit lifecycle events.
 */
export interface IPersona extends EventDispatcher {
  /** Unique persona identifier */
  readonly name: string;

  /** Current enabled state */
  readonly enabled: boolean;

  /** Priority for event handling (higher = first) */
  readonly priority: number;

  /** Map of registered tools (name → tool instance) */
  readonly tools: Map<string, ITool>;

  /** Configuration */
  readonly config: IPersonaConfig;

  // ==================== Lifecycle ====================

  /**
   * Enable the persona
   * - Applies default view configuration
   * - Activates first mode if available
   * - Triggers enabled event
   */
  enable(): void;

  /**
   * Disable the persona
   * - Disables all enabled tools first
   * - Triggers disabled event
   * - Clears current view and mode
   */
  disable(): void;

  // ==================== Mode Management ====================

  /**
   * Get all available modes for this persona
   *
   * @returns Array of persona modes
   */
  getAvailableModes(): IPersonaMode[];

  /**
   * Get currently active mode
   *
   * @returns Current mode or undefined
   */
  getCurrentMode(): IPersonaMode | undefined;

  /**
   * Switch to a different mode
   * - Disables all current tools
   * - Loads new mode's tools
   * - Activates mode's view config if available
   * - Dispatches MODE_CHANGED event
   *
   * @param modeId - Mode identifier
   */
  setMode(modeId: string): void;

  // ==================== Tool Management ====================

  /**
   * Add a tool to this persona
   *
   * @param config - Tool configuration
   * @param skipAutoEnable - If true, skip auto-enabling defaultEnabled tools (used during mode switching)
   * @returns Created tool instance
   * @throws Error if tool name already exists
   */
  addTool(config: IToolConfig, skipAutoEnable?: boolean): ITool;

  /**
   * Remove a tool from this persona
   * Disposes the tool and removes from registry
   *
   * @param name - Tool identifier
   */
  removeTool(name: string): void;

  /**
   * Get a tool by name
   *
   * @param name - Tool identifier
   * @returns Tool instance or undefined
   */
  getTool(name: string): ITool | undefined;

  /**
   * Enable a specific tool
   * Handles exclusive tool logic
   *
   * @param name - Tool identifier
   */
  enableTool(name: string): void;

  /**
   * Disable a specific tool
   *
   * @param name - Tool identifier
   */
  disableTool(name: string): void;

  /**
   * Check if a tool is disabled by default
   *
   * @param toolName - Tool identifier
   * @returns True if tool is disabled by default
   */
  isToolDefaultDisabled(toolName: string): boolean;

  /**
   * Get all tools that are disabled by default
   *
   * @returns Array of tool names
   */
  getDefaultDisabledTools(): string[];

  /**
   * Get all currently enabled tools
   *
   * @returns Array of enabled tools
   */
  getEnabledTools(): ITool[];

  /**
   * Get the default tool for the current mode
   *
   * @returns Default tool instance or undefined
   */
  getDefaultTool(): ITool | undefined;

  /**
   * Reset to the default tool for the current mode
   * Enables the default tool, which (if exclusive) automatically
   * disables all other tools via `enableTool`.
   */
  resetToDefaultTool(): void;

  /**
   * Get toolbar elements for current mode
   * Provides access to the complete toolbar configuration including tools, spacers, and actions
   *
   * @returns Array of toolbar elements or undefined if no mode active
   */
  getToolbarElements(): IToolbarElement[] | undefined;

  // ==================== View Management ====================

  /**
   * Get merged view configuration
   * Combines persona default config with enabled tools' overrides
   *
   * @returns Merged view config
   */
  getViewConfig(): IViewConfig;

  /**
   * Get all available view modes
   * Merges persona views with enabled tool views
   * Tool views with same ID override persona views
   *
   * @returns Array of available view modes
   */
  getAvailableViews(): IViewMode[];

  /**
   * Get currently active view mode
   *
   * @returns Current view mode or undefined
   */
  getCurrentView(): IViewMode | undefined;

  /**
   * Switch to a different view mode
   * - Calls current view's onExit()
   * - Updates current view
   * - Calls new view's onEnter()
   * - Applies view mode settings
   * - ViewManager dispatches VIEW_CHANGED event
   *
   * @param viewId - View mode identifier
   * @param viewContext - Optional view context (e.g., planeId for plane-specific orthographic views)
   */
  setView(viewId: string, viewContext?: IViewMode['viewContext']): void;

  /**
   * Check if the current view can be flipped
   * Returns true for plane-specific or line-specific 2D views
   *
   * @returns True if view can be flipped
   */
  canFlip(): boolean;

  /**
   * Flip the current 2D view (plane or line)
   * Inverts the plane normal or swaps line points, then refreshes the view
   */
  flipView(): void;

  /**
   * Refresh available views based on current tool state
   * Should be called when a tool's view configuration changes dynamically
   */
  refreshAvailableViews(): void;

  // ==================== Cleanup ====================

  /**
   * Cleanup and dispose resources
   * - Removes all tools
   * - Clears state
   * - Removes event listeners
   */
  dispose(): void;
}

/**
 * Events emitted by personas
 *
 * Subscribe to these events to react to persona lifecycle changes.
 * Events are dispatched via EventDispatcher pattern.
 */
export enum PersonaEvents {
  /** Persona was enabled */
  ENABLED = 'persona_enabled',

  /** Persona was disabled */
  DISABLED = 'persona_disabled',

  /** Mode was changed within persona */
  MODE_CHANGED = 'mode_changed',

  /** Tool was added to persona */
  TOOL_ADDED = 'tool_added',

  /** Tool was removed from persona */
  TOOL_REMOVED = 'tool_removed',

  /** Tool was enabled within persona */
  TOOL_ENABLED = 'tool_enabled',

  /** Tool was disabled within persona */
  TOOL_DISABLED = 'tool_disabled',
}

/**
 * Events emitted by tools
 *
 * Subscribe to these events to react to tool state changes.
 * Events are dispatched via EventDispatcher pattern.
 */
export enum ToolEvents {
  /** Tool was enabled */
  ENABLED = 'tool_enabled',

  /** Tool was disabled */
  DISABLED = 'tool_disabled',

  /** Tool state changed (custom state) */
  STATE_CHANGED = 'tool_state_changed',

  /** Temporary hint override (push/pop). Overrides phase-based hints while active. */
  HINT_OVERRIDE = 'tool_hint_override',

  /** Undo/redo availability changed */
  UNDO_REDO_CHANGED = 'tool_undo_redo_changed',
}
